import React, { useState } from "react";
import { FC } from "react";
import Avatar from "shared/Avatar/Avatar";
import DeleteModal from "containers/BlogPage/BlogModal/DeleteModal";
import { useAuth } from "components/AuthLogin/AuthContext";
import { Heart } from "@phosphor-icons/react";
import { animalAvatarUrl } from "utils/animalAvatar";

export interface Comment {
    id: number;
    content_id: number;
    user: {
        id: number;
        userId?: string;
        name?: string;
        displayName?: string;
        avatar: string;
    };
    comment: string;
    created_at: string;
    parent_id: number | null;
    like_count?: number;
    liked?: boolean;
    replies?: Comment[];
}

export interface BlogCommentProps {
    comment: Comment;
    isSmall?: boolean;
    isPostAuthor?: boolean;
    onDelete: () => void;
    onReply: (parentId: number) => void;
}

const timeAgo = (dateStr: string): string => {
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60) return `${diff}초 전`;
    if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}일 전`;
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const BlogComment: FC<BlogCommentProps> = ({
                                               comment,
                                               isSmall,
                                               isPostAuthor = false,
                                               onDelete,
                                               onReply,
                                           }) => {
    const { isAuthenticated, isAdmin, getCookie, fetchWithAuth } = useAuth();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const serverLiked = comment.liked ?? false;
    const localLiked = !serverLiked && localStorage.getItem(`liked_comment_${comment.id}`) === 'true';
    const [liked, setLiked] = useState(serverLiked || localLiked);
    const [likeCount, setLikeCount] = useState(comment.like_count ?? 0);

    const currentUserId = getCookie("userId");
    const isMyComment =
        isAuthenticated &&
        (currentUserId === comment.user?.userId || isAdmin);

    const handleLike = async () => {
        const apiUrl = `${process.env.REACT_APP_API_URL}/posts/${comment.content_id}/comments/${comment.id}/like`;
        if (isAuthenticated) {
            try {
                const res = await fetchWithAuth(apiUrl, { method: "POST" });
                if (!res || !res.ok) return;
                const data = await res.json();
                setLiked(data.liked);
                setLikeCount(data.like_count);
            } catch (e) {
                console.error(e);
            }
        } else {
            const lsKey = `liked_comment_${comment.id}`;
            const nowLiked = !liked;
            try {
                const res = await fetch(apiUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ action: nowLiked ? "like" : "unlike" }),
                });
                if (!res.ok) return;
                const data = await res.json();
                setLiked(nowLiked);
                setLikeCount(data.like_count);
                if (nowLiked) {
                    localStorage.setItem(lsKey, "true");
                } else {
                    localStorage.removeItem(lsKey);
                }
            } catch (e) {
                console.error(e);
            }
        }
    };

    const handleDeleteConfirm = () => {
        onDelete();
        setIsDeleteModalOpen(false);
    };

    return (
        <div className="nc-CommentCard flex gap-3">
            <Avatar
                sizeClass={`flex-shrink-0 ${!isSmall ? "w-8 h-8" : "w-6 h-6"}`}
                imgUrl={
                    comment.user?.userId
                        ? comment.user?.avatar
                        : animalAvatarUrl(comment.user?.displayName ?? comment.user?.name)
                }
            />

            <div className="flex-1 min-w-0">
                {/* Name + author badge + time */}
                <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
            {comment.user?.displayName ?? comment.user?.name ?? "익명"}
          </span>
                    {isPostAuthor && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400 leading-none">
              작성자
            </span>
                    )}
                    <span className="text-xs text-neutral-400 dark:text-neutral-500">
            {timeAgo(comment.created_at)}
          </span>
                </div>

                {/* Body */}
                <p className="mt-1 text-sm text-neutral-700 dark:text-neutral-300 break-words leading-relaxed whitespace-pre-wrap">
                    {comment.comment}
                </p>

                {/* Actions */}
                <div className="flex items-center gap-3 mt-2">
                    <button
                        onClick={handleLike}
                        className={`flex items-center gap-1 text-xs transition-colors cursor-pointer ${
                            liked
                                ? "text-red-500"
                                : "text-neutral-400 dark:text-neutral-500 hover:text-red-400 dark:hover:text-red-400"
                        }`}
                    >
                        <Heart size={14} weight={liked ? "fill" : "regular"} />
                        {likeCount > 0 && <span>{likeCount}</span>}
                    </button>

                    {comment.parent_id === null && isAuthenticated && (
                        <button
                            onClick={() => onReply(comment.id)}
                            className="text-xs text-neutral-400 hover:text-neutral-600 dark:text-neutral-500 dark:hover:text-neutral-300 transition-colors"
                        >
                            답글
                        </button>
                    )}

                    {isMyComment && (
                        <button
                            onClick={() => setIsDeleteModalOpen(true)}
                            className="text-xs text-neutral-400 hover:text-red-500 dark:text-neutral-500 dark:hover:text-red-400 transition-colors ml-auto"
                        >
                            삭제
                        </button>
                    )}
                </div>
            </div>

            <DeleteModal
                isOpen={isDeleteModalOpen}
                title="댓글 삭제"
                mainMessage="이 댓글을 삭제할까요?"
                subMessage="되돌릴 수 없어요."
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteConfirm}
                confirmLabel="삭제"
                cancelLabel="취소"
            />
        </div>
    );
};

export default BlogComment;